import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Twitter } from "lucide-react";
import mascot from "../assets/mascot.webp";
import { scrollToId } from "./SmoothScroll";

const LINKS = [
    { label: "Tokenomics", href: "#tokenomics" },
    { label: "Chart", href: "#chart" },
    { label: "How to Buy", href: "#how-to-buy" },
    { label: "Roadmap", href: "#roadmap" },
    { label: "Crew", href: "#community" },
    { label: "Rewards", href: "/rewards" },
    { label: "Game", href: "/game" },
];

export default function Nav() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (href) => {
        setOpen(false);
        if (href.startsWith("/")) {
            window.location.href = href;
            return;
        }
        setTimeout(() => scrollToId(href), open ? 120 : 0);
    };

    return (
        <header
            className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "border-b border-white/10 bg-[#14060F]/90 sm:backdrop-blur-xl"
                    : "border-b border-transparent bg-transparent"
            }`}
        >
            <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:h-20 sm:px-8">
                <button
                    data-testid="nav-logo-button"
                    onClick={() => go("#top")}
                    className="flex items-center gap-3"
                >
                    <img
                        src={mascot}
                        alt="BLASTARDIO mascot"
                        className="h-9 w-9 rounded-full ring-1 ring-white/20 sm:h-10 sm:w-10"
                    />
                    <span className="font-display text-sm font-bold tracking-wider">
                        BLASTARDIO
                    </span>
                </button>

                <nav className="hidden items-center gap-8 lg:flex">
                    {LINKS.map((l) => (
                        <button
                            key={l.href}
                            data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                            onClick={() => go(l.href)}
                            className="font-mono text-[11px] tracking-[0.2em] text-white/60 transition-colors hover:text-blast-pink"
                        >
                            {l.label.toUpperCase()}
                        </button>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <a
                        data-testid="nav-x-button"
                        href="https://x.com/blastardio"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="BLASTARDIO on X"
                        className="flex items-center justify-center rounded-full border border-white/15 p-2.5 text-white/70 transition-all hover:-translate-y-0.5 hover:border-white hover:text-white"
                    >
                        <Twitter size={14} />
                    </a>
                    <button
                        data-testid="nav-buy-button"
                        onClick={() => go("#how-to-buy")}
                        className="hidden rounded-full bg-blast-pink px-5 py-2.5 font-mono text-[11px] font-bold tracking-[0.15em] text-black transition-all hover:-translate-y-0.5 hover:shadow-[0_0_32px_rgba(255,46,147,0.55)] sm:block"
                    >
                        JOIN PRESALE
                    </button>
                    <button
                        data-testid="nav-mobile-toggle"
                        onClick={() => setOpen(!open)}
                        className="rounded-full border border-white/15 p-2.5 lg:hidden"
                        aria-label="Toggle menu"
                    >
                        {open ? <X size={18} /> : <Menu size={18} />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden border-b border-white/10 bg-[#14060F]/95 backdrop-blur-xl lg:hidden"
                    >
                        <div className="flex flex-col gap-1 px-5 py-4">
                            {LINKS.map((l) => (
                                <button
                                    key={l.href}
                                    onClick={() => go(l.href)}
                                    className="rounded-lg px-3 py-3 text-left font-mono text-xs tracking-[0.2em] text-white/70 hover:bg-white/5 hover:text-blast-pink"
                                >
                                    {l.label.toUpperCase()}
                                </button>
                            ))}
                            <button
                                data-testid="nav-mobile-buy-button"
                                onClick={() => go("#how-to-buy")}
                                className="mt-2 rounded-full bg-blast-pink px-5 py-3 font-mono text-[11px] font-bold tracking-[0.15em] text-black"
                            >
                                JOIN PRESALE
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}
